import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';

import { API_URL } from '../config/api';
import { setUser } from '../store/userSlice';
import LoadingIndicator from '../Components/LoadingIndicator';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (!API_URL) {
        throw new Error('Login service is not configured. Please set REACT_APP_API_URL.');
      }

      const payload = {
        action: 'login',
        email: formData.email.trim(),
        password: formData.password
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.user) {
        const { user, message } = result;
        localStorage.setItem('userid', user.userId ?? '');
        localStorage.setItem('name', user.name ?? '');
        localStorage.setItem('email', user.email ?? '');
        localStorage.setItem('role', user.role ?? '');
        localStorage.setItem('phone', user.phone ?? '');
        localStorage.setItem('companyName', user.companyName ?? '');
        localStorage.setItem('gstNumber', user.gstNumber ?? '');
        localStorage.setItem('profileImage', user.profileImage ?? '');
        localStorage.setItem("role", user.role || "user");
        localStorage.setItem("permissions", JSON.stringify(user.permissions || []));
        dispatch(setUser({
          userId: user.userId ?? '',
          name: user.name ?? '',
          email: user.email ?? '',
          role: user.role ?? '',
          phone: user.phone ?? '',
          companyName: user.companyName ?? '',
          gstNumber: user.gstNumber ?? '',
          profileImage: user.profileImage ?? '',
          permissions: user.permissions || []
        }));
        toast.success(message || 'Login successful');
        navigate('/dashboard');
      } else {
        const errorMessage = result?.message || 'Invalid credentials, please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-left-panel">
        <div className="login-left-brand">
          <span className="login-brand-dot"></span>
          <div>
            <img
              src="/assets/img/Website_logo_color logo-black text-1.png"
              alt="Office logo"
              className="login-brand-logo"
            />
          </div>
        </div>
        <div className="login-left-illustration">
          <img
            src="/assets/img/login.png"
            alt="Login illustration"
            className="login-left-image"
          />
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-right-inner">
          <h5 className="login-heading">Log into your account</h5>
          <form onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="email" className="login-label">Email Address</label>
              <div className="login-input-row">
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`login-input-line ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
              </div>
              {errors.email && (
                <span className="error-message" id="email-error" role="alert">
                  <i className="fas fa-exclamation-circle"></i> {errors.email}
                </span>
              )}
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">Password</label>
              <div className="login-input-row">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`login-input-line ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  className="login-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                </button>
              </div>
              {errors.password && (
                <span className="error-message" id="password-error" role="alert">
                  <i className="fas fa-exclamation-circle"></i> {errors.password}
                </span>
              )}
            </div>

            <div className="login-forgot">
              <Link to="/forget">Forgot Password</Link>
            </div>

            <button
              type="submit"
              className={`login-primary-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <LoadingIndicator size={22} className="login-button-loader" />
                  <span style={{ marginLeft: '8px' }}>Signing...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
